import { createSelector } from "reselect";

import { SubscriptionPhase } from "constants/subscription-phase";
import { IStoreState } from "interfaces/storeState";
import { createRequestErrorSelector, createRequestFetchingSelector } from "store/requests/selectors";

import { selectCurrentSubscription, selectSubscriptionPhase } from "../subscriptions";

import { RecurlyRequestFetchingSelectorsNames } from "./actions";
import { IRecurlyCoupon } from "./interfaces";
import { IRecurlyState } from "./reducer";

export interface IWithRecurlyEntitiesState {
  entities: {
    recurly: IRecurlyState;
  };
}

export const getIsConfiguringRecurly = (state: IStoreState): boolean =>
  createRequestFetchingSelector([RecurlyRequestFetchingSelectorsNames.CONFIGURE_RECURLY_REQUEST])(state);

export const getConfigureRecurlyError = (state: IStoreState): string =>
  createRequestErrorSelector([RecurlyRequestFetchingSelectorsNames.CONFIGURE_RECURLY_REQUEST])(state);

export const getIsCheckingRecurlyCoupon = (state: IStoreState): boolean =>
  createRequestFetchingSelector([RecurlyRequestFetchingSelectorsNames.CHECK_RECURLY_COUPON_REQUEST])(state);

export const getCheckRecurlyCouponError = (state: IStoreState): string =>
  createRequestErrorSelector([RecurlyRequestFetchingSelectorsNames.CHECK_RECURLY_COUPON_REQUEST])(state);

export const getIsFetchingCoupons = (state: IStoreState): boolean =>
  createRequestFetchingSelector([RecurlyRequestFetchingSelectorsNames.FETCHING_COUPONS_REQUEST])(state);

export const getShouldFetchActiveCoupon = (state: IWithRecurlyEntitiesState): boolean => {
  return state.entities.recurly.shouldFetchActiveCoupon;
};

export const getRecurlyCoupon = (state: IWithRecurlyEntitiesState): IRecurlyCoupon | null => {
  return state.entities.recurly.coupon;
};

export const getRecurlyActiveCoupon = (state: IWithRecurlyEntitiesState): IRecurlyCoupon | null => {
  const recurlyCoupon = state.entities.recurly.coupon;
  const isCouponActive = recurlyCoupon?.redemptions?.some(redemption => redemption.state === "active");
  return isCouponActive ? recurlyCoupon : null;
};

export const getRecurlyNewCoupon = (state: IWithRecurlyEntitiesState): IRecurlyCoupon | null => {
  return state.entities.recurly.newCoupon;
};

export const selectRecurlyCoupon = createSelector(
  selectCurrentSubscription,
  selectSubscriptionPhase,
  getRecurlyCoupon,
  (subscription, subscriptionPhase, coupon) => {
    if (!coupon) {
      return null;
    }

    if (subscriptionPhase === SubscriptionPhase.Paid) {
      if (coupon.appliesForMonths && coupon.appliesForMonths < subscription.billingCycle) {
        return null;
      }
      return coupon;
    }
    if (subscriptionPhase === SubscriptionPhase.ExpiredPaid && coupon.redeemed === true) {
      return null;
    }

    return coupon;
  }
);
